import { z } from 'zod';
import { GameRecordSchema } from './game';
import { GameSkinSchema } from './skin';
import { EventStatusSchema } from './event';
import { MessageAttachmentsDataSchema } from './media';
import { bingoCardSchema } from './bingo';

export const FileUploadSchema = z.object({ id: z.string(), filename: z.string(), mimeType: z.string() });
export type FileUpload = z.infer<typeof FileUploadSchema>;

export const TeamDataSchema = z.object({
	lat: z.number().default(0),
	lng: z.number().default(0),
	heading: z.number().nullable().default(null),
	timestamp: z.number().default(0), //Timestamp for location update
	bonusPoints: z.number().default(0),
	avatar: FileUploadSchema.nullish(),
	members: z
		.array(
			z.object({
				id: z.string(),
				name: z.string(),
			}),
		)
		.default([]),
	devices: z
		.array(
			z.object({
				id: z.string(),
				name: z.string().optional(),
			}),
		)
		.default([]),
	// used when game type is double-dealer, refers to id of a team member
	ratId: z.string().nullish(),
});
export type TeamData = z.infer<typeof TeamDataSchema>;

export const TeamRecordSchema = z.object({
	id: z.string(),
	name: z.string(),
	rejoinCode: z.string().nullish(),
	captainId: z.string(),
	score: z.number(),
	data: TeamDataSchema.default(TeamDataSchema.parse({})),
});
export type TeamRecord = z.infer<typeof TeamRecordSchema>;

export const ChatMessageSchema = z.object({
	id: z.string(),
	teamId: z.string().nullable(),
	message: z.string(),
	attachments: MessageAttachmentsDataSchema.optional().default([]),
	channelId: z.string().nullable(),
	_created_at: z.preprocess((arg) => {
		if (typeof arg === 'string') return new Date(arg);
		return arg;
	}, z.date()),
});
export type ChatMessageRecord = z.infer<typeof ChatMessageSchema>;

export const RoundStateSchema = z.object({
	roundId: z.string(),
	teamId: z.string().nullable(),
	unlocked: z.boolean(),
});
export type RoundState = z.infer<typeof RoundStateSchema>;

export const TaskStatusSchema = z.enum(['todo', 'in-progress', 'done']);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskAnswerSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('text'), text: z.string() }),
	z.object({ kind: z.literal('single-choice'), chosenId: z.string() }),
	z.object({ kind: z.literal('multiple-choice'), chosenIds: z.array(z.string()) }),
	z.object({ kind: z.literal('video'), ...FileUploadSchema.shape, message: z.string().optional() }),
	z.object({ kind: z.literal('photo'), ...FileUploadSchema.shape, message: z.string().optional() }),
	z.object({
		kind: z.literal('bingo'),
		card: bingoCardSchema,
		checked: z.array(z.number()).max(25).default([]),
		bingo: z.boolean().default(false),
	}),
]);
export type TaskAnswer = z.infer<typeof TaskAnswerSchema>;
type TaskAnswerOf<K extends TaskAnswer['kind']> = Extract<TaskAnswer, { kind: K }>;
export type TaskAnswerText = TaskAnswerOf<'text'>;
export type TaskAnswerSingleChoice = TaskAnswerOf<'single-choice'>;
export type TaskAnswerMultipleChoice = TaskAnswerOf<'multiple-choice'>;
export type TaskAnswerVideo = TaskAnswerOf<'video'>;
export type TaskAnswerPhoto = TaskAnswerOf<'photo'>;

export const TaskStateSchema = z.object({
	status: TaskStatusSchema,
	unlocked: z.boolean().default(false),
	taskId: z.string(),
	answer: TaskAnswerSchema.optional(),
	attempts: z.number().default(0),
	pointsGiven: z.number(),
	completionResponse: z.string().nullish(),
	updatedAt: z.string().datetime().optional(),
});
export type TaskState = z.infer<typeof TaskStateSchema>;
export type TaskStateLocal = TaskState & { isLocal?: boolean };

export const TaskStateRecordSchema = TaskStateSchema.extend({ teamId: z.string().nullable(), eventId: z.string() });
export type TaskStateRecord = z.infer<typeof TaskStateRecordSchema>;

export const ClientInitSchema = z.object({
	game: GameRecordSchema,
	eventId: z.string(),
	eventStatus: EventStatusSchema,
	eventFrozen: z.boolean(),
	teamId: z.string(),
	teams: z.array(TeamRecordSchema),
	chatMessages: z.object({
		global: z.array(ChatMessageSchema),
		'game-master': z.array(ChatMessageSchema),
	}),
	roundStates: z.record(z.string(), RoundStateSchema),
	taskStates: z.record(z.string(), TaskStateSchema),
	skin: GameSkinSchema.optional(),
});
export type ClientInit = z.infer<typeof ClientInitSchema>;
